import React from 'react';
import { Link } from 'react-router-dom';

import {
  TextInput,
  FormItem,
  Form,
  FormGroup,
  Tag,
  Loading,
} from 'carbon-components-react';

import CustomButton from '../../../components/custom-button/custom-btn.component';
import BorderedLogo from '../../../components/bordered-logo/bordered-logo.component';
import Footer from '../../../components/footer/footer.component';

import registerBg from '../../../assets/images/register_bg.png';
import Slider from '../../../components/slider/slider.component';

import './register.styles.scss';

import {
  registerSchool,
  sendRegistrationOtp,
} from '../../../api/school.service';
import {
  isEmail,
  isRegistrationPhoneNumber,
  normalizePhoneNumber,
} from '@/utils/common.util';

// how long the code stays valid, and how long before another one can be requested.
// both mirror the API settings - the resend cooldown is enforced server side too,
// where an early resend answers with success but deliberately sends no SMS
const OTP_VALIDITY_SECONDS = 10 * 60;
const OTP_RESEND_COOLDOWN_SECONDS = 60;
const OTP_CODE_LENGTH = 6;

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

class RegisterPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      name: '',
      location: '',
      emailAddress: '',
      firstName: '',
      lastName: '',
      telephone: '',
      requestInProgress: false,
      showSuccess: false,
      errorMessage: null,
      btnText: 'REGISTER',

      // phone verification
      otpCode: '',
      codeSent: false,
      sendingCode: false,
      otpMessage: null,
      otpErrorMessage: null,
      secondsUntilExpiry: 0,
      secondsUntilResend: 0,
    };

    this.otpTimer = null;
  }

  componentWillUnmount() {
    this.clearOtpTimer();
  }

  clearOtpTimer = () => {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
      this.otpTimer = null;
    }
  };

  // one ticker drives both countdowns: how long the code is still valid,
  // and how long until another one can be requested
  startOtpTimer = () => {
    this.clearOtpTimer();

    this.otpTimer = setInterval(() => {
      this.setState((previousState) => {
        const secondsUntilExpiry = Math.max(
          previousState.secondsUntilExpiry - 1,
          0,
        );
        const secondsUntilResend = Math.max(
          previousState.secondsUntilResend - 1,
          0,
        );

        if (secondsUntilExpiry === 0) {
          this.clearOtpTimer();
        }

        return { secondsUntilExpiry, secondsUntilResend };
      });
    }, 1000);
  };

  handleChange = (event) => {
    // destructure value and name of event.target
    const { value, name } = event.target;

    // update state
    this.setState({ [name]: value });
  };

  // the code is issued against one specific number, so editing the number
  // invalidates it - drop back to the unverified state rather than letting a
  // code be submitted against a number it was never sent to
  handleTelephoneChange = (event) => {
    const { value } = event.target;

    this.clearOtpTimer();

    this.setState({
      telephone: value,
      codeSent: false,
      otpCode: '',
      otpMessage: null,
      otpErrorMessage: null,
      errorMessage: null,
      secondsUntilExpiry: 0,
      secondsUntilResend: 0,
    });
  };

  handleOtpCodeChange = (event) => {
    // digits only, capped at the code length
    const otpCode = event.target.value
      .replace(/\D/g, '')
      .slice(0, OTP_CODE_LENGTH);

    this.setState({ otpCode, otpErrorMessage: null, errorMessage: null });
  };

  canSendCode = () =>
    isRegistrationPhoneNumber(this.state.telephone) &&
    !this.state.sendingCode &&
    this.state.secondsUntilResend === 0;

  sendCode = async () => {
    if (!this.canSendCode()) return;

    this.setState({
      sendingCode: true,
      otpMessage: null,
      otpErrorMessage: null,
      errorMessage: null,
    });

    const phoneNumber = normalizePhoneNumber(this.state.telephone);
    const response = await sendRegistrationOtp(phoneNumber);

    if (!response.success) {
      this.setState({ sendingCode: false, otpErrorMessage: response.message });
      return;
    }

    this.setState({
      sendingCode: false,
      codeSent: true,
      otpMessage: `We sent a ${OTP_CODE_LENGTH}-digit code to ${phoneNumber}.`,
      secondsUntilExpiry: OTP_VALIDITY_SECONDS,
      secondsUntilResend: OTP_RESEND_COOLDOWN_SECONDS,
    });

    this.startOtpTimer();
  };

  // validate data entered
  validateData = () =>
    this.state.name.length > 0 &&
    this.state.location.length > 0 &&
    this.state.firstName.length > 0 &&
    this.state.lastName.length > 0 &&
    isRegistrationPhoneNumber(this.state.telephone) &&
    isEmail(this.state.emailAddress) &&
    this.state.codeSent &&
    this.state.otpCode.length === OTP_CODE_LENGTH;

  // Register event handler
  register = async (event) => {
    event.preventDefault();

    // update state to set request in progress to true and error occurred to false
    this.setState({
      requestInProgress: true,
      errorMessage: null,
      btnText: null,
    });

    // send the number in the same form it was verified in
    const response = await registerSchool({
      ...this.state,
      telephone: normalizePhoneNumber(this.state.telephone),
      otpCode: this.state.otpCode,
    });

    // check response
    if (!response.success) {
      // registration failed, change state to show error message
      this.setState({
        requestInProgress: false,
        btnText: 'REGISTER',
        errorMessage: response.message,
      });

      return;
    }

    // set state to show registration success
    this.clearOtpTimer();
    this.setState({ showSuccess: true });
  };

  render() {
    return (
      <div className="register">
        <div className="bx--row">
          <div
            className="bx--col-md-5"
            style={{ zIndex: 100, background: '#f9f9fb' }}
          >
            <div
              className=""
              style={{
                width: '700px',
                padding: '0 20px',
                maxWidth: '95%',
                margin: '0 auto',
              }}
            >
              <div className="mt-60 mb-50">
                <BorderedLogo />
              </div>
              <div hidden={this.state.showSuccess}>
                <h3 className="text-primary font-bold text-24 mb-30">
                  Register your school
                </h3>

                <Form action="post">
                  <FormGroup legendText="">
                    <FormItem>
                      <TextInput
                        id="name"
                        name="name"
                        labelText="Name of your school"
                        placeholder="eg. Abbys International Academy"
                        value={this.state.name}
                        onChange={this.handleChange}
                      />
                    </FormItem>
                  </FormGroup>
                  <div className="bx--row">
                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        <FormItem>
                          <TextInput
                            id="location"
                            name="location"
                            labelText="School location"
                            placeholder="Where your school is located"
                            value={this.state.location}
                            onChange={this.handleChange}
                          />
                        </FormItem>
                      </FormGroup>
                    </div>

                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        <FormItem>
                          <TextInput
                            id="email"
                            name="emailAddress"
                            labelText="Email address"
                            placeholder="Your school or your personal email address"
                            value={this.state.emailAddress}
                            onChange={this.handleChange}
                          />
                        </FormItem>

                        <p className="text-grey-darker text-12 nmb">
                          A valid working email is required
                        </p>
                      </FormGroup>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        <FormItem>
                          <TextInput
                            id="firstname"
                            name="firstName"
                            labelText="First name"
                            placeholder="Enter your first name"
                            value={this.state.firstName}
                            onChange={this.handleChange}
                          />
                        </FormItem>

                        <p className="text-12 text-grey-dark">
                          An account will be created for you as the school's
                          administrator
                        </p>
                      </FormGroup>
                    </div>

                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        <FormItem>
                          <TextInput
                            id="lastname"
                            name="lastName"
                            labelText="Last name"
                            placeholder="Enter your last name"
                            value={this.state.lastName}
                            onChange={this.handleChange}
                          />
                        </FormItem>
                      </FormGroup>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        <FormItem>
                          <TextInput
                            id="telephone"
                            name="telephone"
                            type="text"
                            inputMode="tel"
                            labelText="Phone number"
                            placeholder="e.g. 0241234567"
                            value={this.state.telephone}
                            onChange={this.handleTelephoneChange}
                            readOnly={this.state.codeSent}
                            invalid={
                              this.state.telephone.length > 0 &&
                              !isRegistrationPhoneNumber(this.state.telephone)
                            }
                            invalidText="Enter a valid phone number, e.g. 0241234567 or +233241234567"
                          />
                        </FormItem>

                        <p className="text-grey-darker text-12 nmb">
                          We will text a verification code to this number
                        </p>
                      </FormGroup>
                    </div>

                    <div className="bx--col-md-4">
                      <FormGroup legendText="">
                        {/* before a code is sent: request one */}
                        <div hidden={this.state.codeSent}>
                          <CustomButton
                            classes="primary f-width mt-25"
                            onClick={this.sendCode}
                            disabled={!this.canSendCode()}
                            isLoading={this.state.sendingCode}
                          >
                            SEND VERIFICATION CODE
                          </CustomButton>
                        </div>

                        {/* after a code is sent: enter it, or start over */}
                        <div hidden={!this.state.codeSent}>
                          <FormItem>
                            <TextInput
                              id="otpCode"
                              name="otpCode"
                              type="text"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              labelText="Verification code"
                              placeholder={`${OTP_CODE_LENGTH}-digit code`}
                              value={this.state.otpCode}
                              onChange={this.handleOtpCodeChange}
                            />
                          </FormItem>

                          <p className="text-grey-darker text-12 nmb">
                            {this.state.secondsUntilExpiry > 0 ? (
                              <>
                                {this.state.otpMessage} Expires in{' '}
                                {formatCountdown(this.state.secondsUntilExpiry)}
                              </>
                            ) : (
                              <>This code has expired. Request a new one.</>
                            )}
                          </p>

                          <div className="mt-10">
                            <button
                              type="button"
                              className="link-btn text-primary text-12 font-bold"
                              onClick={this.sendCode}
                              disabled={!this.canSendCode()}
                            >
                              {this.state.secondsUntilResend > 0
                                ? `Resend code in ${formatCountdown(
                                    this.state.secondsUntilResend,
                                  )}`
                                : 'Resend code'}
                            </button>
                            &nbsp;&middot;&nbsp;
                            <button
                              type="button"
                              className="link-btn text-primary text-12 font-bold"
                              onClick={() =>
                                this.handleTelephoneChange({
                                  target: { value: this.state.telephone },
                                })
                              }
                            >
                              Change number
                            </button>
                          </div>
                        </div>

                        <div hidden={this.state.otpErrorMessage == null}>
                          <Tag className="tag-f-w mt-10" type="red">
                            {this.state.otpErrorMessage}
                          </Tag>
                        </div>
                      </FormGroup>
                    </div>
                  </div>
                  <div className="bx--row">
                    <div className="bx--col-md-4">
                      <CustomButton
                        classes="primary f-width mt-10 mb-40"
                        onClick={this.register}
                        type="submit"
                        disabled={
                          !this.validateData() || this.state.requestInProgress
                        }
                      >
                        {this.state.btnText}

                        <div hidden={this.state.btnText != null}>
                          <div className="d-flex-center">
                            <Loading
                              withOverlay={false}
                              small={true}
                              description="Please wait..."
                            />
                          </div>
                        </div>
                      </CustomButton>
                    </div>
                  </div>
                  <Link
                    to="/account/login"
                    className="text-primary text-14 font-bold"
                  >
                    Log in
                  </Link>
                  &nbsp;
                  <span className="text-grey-darker text-14">
                    to existing account
                  </span>
                  <div hidden={this.state.errorMessage == null}>
                    <Tag className="tag-f-w mt-30" type="red">
                      {this.state.errorMessage}
                    </Tag>
                  </div>
                </Form>
              </div>

              <div className="success" hidden={!this.state.showSuccess}>
                <h3 className="text-primary font-bold text-24 mt-100 mb-30">
                  Registration Successful!
                </h3>
                <p className="text-16 text-grey-dark mt-20">
                  Thank you for registering your school on&nbsp;
                  <span className="font-bold">
                    EPOBS Comprehensive School Suite (CSS).
                  </span>
                  <br />A confirmation email is sent to&nbsp;
                  <span className="text-primary">
                    {this.state.emailAddress.toLowerCase()}.
                  </span>
                  <br />
                  Follow the link in the email to verify your account. Thank
                  you!
                </p>
                <div className="bx--row">
                  <div className="bx--col-md-4">
                    <CustomButton classes="primary f-width mt-50 mb-40">
                      <a
                        href="https://www.epobssuite.com/"
                        className="no-decoration text-white"
                      >
                        VISIT OUR WEBSITE TO KNOW MORE ABOUT EPOBS CSS
                      </a>
                    </CustomButton>
                  </div>
                </div>
              </div>

              <Footer />
            </div>
          </div>
          <div className="bx--col-md-3 fixed-right padding-0">
            <div className="bx--row">
              <Slider />
            </div>
            <img className="img-f-w-obj-cover" src={registerBg} alt="" />
          </div>
        </div>
      </div>
    );
  }
}

export default RegisterPage;
